import { EToastVariant, Popover } from '@outdoorsyco/bonfire';
import { useCallback, useRef, useState } from 'react';
import { VisuallyHidden } from 'react-aria';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useToast } from '@/hooks/useToast';
import { updateUser } from '@/redux/modules/auth';
import { IFullUserInfo } from '@/services/types/user/user';
import { uploadImage } from '@/services/uploadImage';

import { DeletePhotoModal } from '../../DeletePhotoModal/DeletePhotoModal';

interface IProfilePhotoMenuButtonProps {
  userData: IFullUserInfo;
  showPopover: boolean;
  loading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  refetchUser: () => void;
}

export const ProfilePhotoMenuButton = ({
  userData,
  showPopover,
  loading,
  setIsUploading,
  refetchUser,
}: IProfilePhotoMenuButtonProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleClick = () => {
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  const submitFiles = useCallback(
    async (file: File) => {
      if (file) {
        try {
          setIsUploading(true);
          const result = await uploadImage(file, `users/${userData.id}/images`);
          const data = {
            avatar_url: result.secure_url,
          };

          await dispatch(updateUser(data));
          await refetchUser();

          setIsUploading(false);
          showToast(
            EToastVariant.Success,
            intl.formatMessage({
              defaultMessage: 'Profile photo uploaded',
              id: 'iPAMUl',
            }),
            intl.formatMessage({
              defaultMessage: 'Profile photo uploaded successfully.',
              id: 'ab50l5',
            }),
          );
        } catch {
          setIsUploading(false);
          showToast(
            EToastVariant.Error,
            intl.formatMessage({
              defaultMessage: 'Error',
              id: 'KN7zKn',
            }),
            intl.formatMessage({
              defaultMessage: 'Oops, something went wrong. Try again later.',
              id: '/m4g4K',
            }),
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateUser, uploadImage],
  );

  const handleDelete = async () => {
    try {
      const data = {
        avatar_url: '',
      };
      setIsUploading(true);
      await dispatch(updateUser(data));
      await refetchUser();
      setIsUploading(false);
      setOpenDeleteModal(false);

      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Profile photo deleted',
          id: 'ek7/Vr',
        }),
        intl.formatMessage({
          defaultMessage: 'Profile photo deleted successfully.',
          id: 'GUfMbP',
        }),
      );
    } catch {
      setIsUploading(false);
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Oops, something went wrong. Try again later.',
          id: '/m4g4K',
        }),
      );
    }
  };

  const handleOnFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        submitFiles(event.target.files[0]);
      }
      event.target.value = ''; // Allow same file selection
    },
    [submitFiles],
  );

  return (
    <div className={`absolute top-3 right-3 ${!showPopover && '-z-50'}`}>
      <VisuallyHidden>
        <input
          ref={inputRef}
          id="profile-photo-uploader"
          type="file"
          accept="image/*"
          multiple={false}
          onChange={handleOnFileSelect}
        />
      </VisuallyHidden>
      {showPopover && (
        <Popover>
          <Popover.Option
            value="upload"
            label={intl.formatMessage({
              defaultMessage: 'Upload',
              id: 'p4N05H',
            })}
            aria-label={intl.formatMessage({
              defaultMessage: 'Upload photo button',
              id: 'lljmDR',
            })}
            onClick={handleClick}
            disabled={loading}
          />
          {userData.profile.avatar_url ? (
            <Popover.Option
              value="delete"
              label={intl.formatMessage({
                defaultMessage: 'Delete',
                id: 'K3r6DQ',
              })}
              aria-label={intl.formatMessage({
                defaultMessage: 'Delete photo button',
                id: '03J4Si',
              })}
              onClick={() => setOpenDeleteModal(true)}
              disabled={loading}
            />
          ) : undefined}
        </Popover>
      )}
      {openDeleteModal && (
        <DeletePhotoModal
          title={intl.formatMessage({
            defaultMessage: 'Are you sure you want to delete the profile photo?',
            id: 'L21CNl',
          })}
          isOpen={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onDeleteHandler={handleDelete}
          disableActions={loading}
        />
      )}
    </div>
  );
};
